/**
 * FormErrors Component
 * ====================
 * Displays all validation errors for a form.
 * Shows form-level errors and a summary of field errors.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/solid';

interface FormErrorsProps {
  errors: Record<string, string[]>;
  className?: string;
  showCheckmark?: boolean;
  isValid?: boolean;
}

/**
 * FormErrors: Displays all form validation errors
 */
const FormErrors: React.FC<FormErrorsProps> = ({
  errors,
  className,
  showCheckmark = false,
  isValid = false,
}) => {
  const { t } = useTranslation();
  const errorEntries = Object.entries(errors).filter(([key]) => key !== '_form');
  const formLevelErrors = errors._form || [];
  const totalErrors = formLevelErrors.length + errorEntries.length;

  if (totalErrors === 0 && !isValid) {
    return null;
  }

  if (isValid && showCheckmark) {
    return (
      <div
        className={clsx(
          'flex items-start gap-3 p-4 rounded-lg',
          'bg-success-50 dark:bg-success-900/20',
          'border border-success-200 dark:border-success-800',
          'text-success-800 dark:text-success-200',
          className
        )}
        role="status"
        aria-live="polite"
      >
        <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <span>{t('All validations passed!')}</span>
      </div>
    );
  }

  if (totalErrors === 0) {
    return null;
  }

  return (
    <div
      className={clsx(
        'space-y-3 p-4 rounded-lg',
        'bg-danger-50 dark:bg-danger-900/20',
        'border border-danger-200 dark:border-danger-800',
        'text-danger-800 dark:text-danger-200',
        className
      )}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div className="flex-1">
          <h3 className="text-sm mb-2">
            {totalErrors === 1 ? t('1 error found') : `${totalErrors} ${t('errors found')}`}
          </h3>

          {/* Form-level errors */}
          {formLevelErrors.length > 0 && (
            <ul className="space-y-1 text-sm">
              {formLevelErrors.map((error, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="block w-1 h-1 rounded-full mt-1.5 flex-shrink-0 bg-current" />
                  <span>{error}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Field-level errors summary */}
          {errorEntries.length > 0 && (
            <div className={clsx(formLevelErrors.length > 0 && 'mt-3')}>
              <div className="text-xs opacity-75 mb-2">Fields with errors:</div>
              <ul className="space-y-1 text-sm ml-4">
                {errorEntries.map(([field, fieldErrors]) => (
                  <li key={field} className="flex items-start gap-2">
                    <span className="block w-1 h-1 rounded-full mt-1.5 flex-shrink-0 bg-current" />
                    <span>
                      <span>{field}:</span> {fieldErrors[0]}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormErrors;
